import React from 'react';
import {
    TrendingUp, TrendingDown, Minus, Clock, Gauge, Target,
    BarChart3, Activity, CheckCircle2
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';

// ============================================
// TYPES for DACAMS-based 4-Scenario Results
// ============================================

export interface ScenarioKPIs {
    scenario_id: string;
    scenario_name: string;
    description: string;
    enabled_features: string[];
    rac: number;
    cc: number;
    axot_mean: number;
    axot_p95: number;
    axit_mean: number;
    otp: number;
    bci: number;
    nss: number;
    delta_rac: number;
    delta_cc: number;
    delta_axot: number;
    delta_otp: number;
    delta_bci: number;
    delta_nss: number;
}

export interface SimulationResults {
    scenarios: {
        s1_asis: ScenarioKPIs;
        s2_ams: ScenarioKPIs;
        s3_fastgate: ScenarioKPIs;
        s4_infrastructure: ScenarioKPIs;
    };
    baseline_id: string;
    scenario_config: {
        ams_enabled: boolean;
        fastgate_enabled: boolean;
        infra_changes_enabled: boolean;
    };
}

interface SimulationResultsTableProps {
    results: SimulationResults | null;
}

// ============================================
// COMPONENT
// ============================================

export const SimulationResultsTable: React.FC<SimulationResultsTableProps> = ({ results }) => {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const isDark = theme === 'dark';

    const textMain = 'text-[var(--text-primary)]';
    const textSub = 'text-[var(--text-secondary)]';
    const cardBg = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-[var(--border)]';
    const headerBg = 'bg-[var(--bg)]';

    if (!results || !results.scenarios) {
        return (
            <div className={`rounded-xl border p-8 text-center ${cardBg}`}>
                <p className={textSub}>
                    {language === 'es'
                        ? 'No hay resultados disponibles. Ejecuta la simulación primero.'
                        : 'No results available. Run the simulation first.'}
                </p>
            </div>
        );
    }

    // Convert scenarios to array for iteration
    const scenarioOrder = ['s1_asis', 's2_ams', 's3_fastgate', 's4_infrastructure'] as const;
    const scenarios = scenarioOrder.map(id => results.scenarios[id]).filter(Boolean);

    if (scenarios.length === 0) {
        return (
            <div className={`rounded-xl border p-8 text-center ${cardBg}`}>
                <p className={textSub}>
                    {language === 'es'
                        ? 'No hay escenarios disponibles en los resultados.'
                        : 'No scenarios found in simulation results.'}
                </p>
            </div>
        );
    }

    // KPI definitions
    const KPI_DEFS = [
        { key: 'rac', deltaKey: 'delta_rac', label: 'RAC', unit: 'mov/h', icon: Gauge, inverse: false },
        { key: 'cc', deltaKey: 'delta_cc', label: 'CC', unit: 'mov/h', icon: Target, inverse: false },
        { key: 'axot_mean', deltaKey: 'delta_axot', label: 'Taxi-Out μ', unit: 'min', icon: Clock, inverse: true },
        { key: 'axot_p95', deltaKey: 'delta_axot', label: 'Taxi-Out p95', unit: 'min', icon: Clock, inverse: true },
        { key: 'otp', deltaKey: 'delta_otp', label: 'OTP', unit: '%', icon: CheckCircle2, inverse: false },
        { key: 'bci', deltaKey: 'delta_bci', label: 'BCI', unit: '', icon: BarChart3, inverse: false },
        { key: 'nss', deltaKey: 'delta_nss', label: 'SI Peak', unit: '%', icon: Activity, inverse: true },
    ];

    // Find best scenario (highest RAC)
    const bestScenario = scenarios.reduce((best, s) =>
        s.rac > best.rac ? s : best, scenarios[0]
    );

    // Delta display component
    const DeltaDisplay = ({ delta, inverse = false }: { delta: number | undefined | null; inverse?: boolean }) => {
        if (delta == null || isNaN(delta) || Math.abs(delta) < 0.1) {
            return <span className={textSub}>—</span>;
        }
        const isPositive = inverse ? delta < 0 : delta > 0;
        return (
            <span className={`flex items-center gap-1 text-xs font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
            </span>
        );
    };

    return (
        <div className={`rounded-xl border overflow-hidden ${cardBg}`}>
            {/* HEADER */}
            <div className={`p-4 border-b ${'border-[var(--border)]'} ${headerBg}`}>
                <h3 className={`text-lg font-bold ${textMain}`}>
                    {language === 'es' ? 'Comparativa de Escenarios' : 'Scenario Comparison'}
                </h3>
                <p className={`text-sm ${textSub}`}>
                    {language === 'es'
                        ? 'KPIs calculados desde DACAMS. Verde = mejora, Rojo = empeora.'
                        : 'KPIs calculated from DACAMS. Green = improvement, Red = degradation.'}
                </p>
            </div>

            {/* TABLE */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className={headerBg}>
                            <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${textSub}`}>
                                KPI
                            </th>
                            {scenarios.map(scenario => (
                                <th key={scenario.scenario_id} className={`px-4 py-3 text-center text-xs font-bold uppercase tracking-wider ${textSub}`}>
                                    <div className="flex flex-col items-center">
                                        <span className={scenario.scenario_id === 's1_asis' ? 'text-[var(--text-tertiary)]' : 'text-emerald-500'}>
                                            {scenario.scenario_name}
                                        </span>
                                        {scenario.scenario_id === 's1_asis' && (
                                            <span className="text-[10px] font-normal text-[var(--text-secondary)]">BASELINE</span>
                                        )}
                                        {scenario.enabled_features.length > 0 && scenario.scenario_id !== 's1_asis' && (
                                            <div className="flex gap-1 mt-1 flex-wrap justify-center">
                                                {scenario.enabled_features.slice(0, 3).map(opt => (
                                                    <span key={opt} className="px-1.5 py-0.5 text-[10px] bg-gray-500/20 rounded">
                                                        {opt}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {KPI_DEFS.map((kpi, idx) => (
                            <tr
                                key={kpi.key}
                                className={`border-t ${'border-[var(--border)]'} ${idx % 2 === 0 ? (isDark ? 'bg-white' : 'bg-white') : (isDark ? 'bg-[#3A7CA5]/30' : 'bg-gray-50')}`}
                            >
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <kpi.icon size={16} className={textSub} />
                                        <div>
                                            <span className={`font-medium ${textMain}`}>{kpi.label}</span>
                                            <span className={`text-xs ml-2 ${textSub}`}>{kpi.unit}</span>
                                        </div>
                                    </div>
                                </td>
                                {scenarios.map(scenario => {
                                    const value = scenario[kpi.key as keyof ScenarioKPIs] as number | undefined | null;
                                    const delta = scenario[kpi.deltaKey as keyof ScenarioKPIs] as number | undefined | null;
                                    const isBaseline = scenario.scenario_id === 's1_asis';

                                    const displayValue = value != null && !isNaN(value as number)
                                        ? (kpi.key === 'bci' ? (value as number).toFixed(2) : (value as number).toFixed(1))
                                        : '—';

                                    return (
                                        <td key={scenario.scenario_id} className="px-4 py-3 text-center">
                                            <div className="flex flex-col items-center">
                                                <span className={`text-lg font-bold ${isBaseline ? textSub : textMain}`}>
                                                    {displayValue}
                                                </span>
                                                {!isBaseline && (
                                                    <DeltaDisplay delta={delta} inverse={kpi.inverse} />
                                                )}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* RECOMMENDATION */}
            {scenarios.length > 1 && (
                <div className={`p-4 border-t ${isDark ? 'border-[var(--border)] bg-emerald-900/20' : 'border-[var(--border)] bg-emerald-50'}`}>
                    <div className="flex items-center gap-2">
                        <TrendingUp className="text-emerald-500" size={20} />
                        <span className={`font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                            {language === 'es' ? 'Mejor Escenario:' : 'Best Scenario:'}
                        </span>
                        <span className={`font-bold ${textMain}`}>
                            {bestScenario.scenario_name}
                        </span>
                        <span className={textSub}>
                            ({language === 'es' ? 'mayor RAC' : 'highest RAC'}: {bestScenario.rac != null ? bestScenario.rac.toFixed(1) : '—'} mov/h)
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SimulationResultsTable;
